import { queueableSendRequest } from '../requestsQueue'
import type { ParsedQuery } from 'query-string'
import type APIThirdPartyPayment from '../../types/APIThirdPartyPayment'
import { createUrl, getECommerceDomainByEnv, getTenantInfo, getWruec, sendRequest } from '../utils'
import type { BackendChannel } from 'utils/constants'

const apiPayments = {
	// Note we receive a top level payment plus an array of otherPayments
	getThirdPartyPayments: async (
		parsed: ParsedQuery,
		Account: any,
		channelType: BackendChannel
	): Promise<
		{
			error: boolean
			msg: string
			otherPayments: APIThirdPartyPayment[]
		} & APIThirdPartyPayment
	> => {
		if (!parsed.wru) {
			throw new Error('apiPayments.getThirdPartyPayments: missing parsed.wru')
		}
		if (!parsed.wruec) {
			parsed.wruec = getWruec({ wru: parsed.wru })
		}
		if (!parsed.request) {
			throw new Error('apiPayments.getThirdPartyPayments: missing parsed.request')
		}
		const url = createUrl(getECommerceDomainByEnv(), `/v3/orders/current/available-payment-methods`)
		const sessionId = parsed.request
		const authHeaders = await Account.getAuthorizationHeader(channelType)
		const tenantId = getTenantInfo()

		return queueableSendRequest(sendRequest)(false, url, 'get', undefined, {
			'tictuk-session-id': sessionId,
			'tictuk-tenant-id': tenantId,
			...authHeaders,
		})
	},
	getSavedCreditCards: async (tenantId: string, Account: any, channelType: BackendChannel): Promise<any> => {
		if (!tenantId) {
			throw new Error('apiPayments.getSavedCreditCards: missing tenant ID')
		}
		const authHeader = await Account.getAuthorizationHeader(channelType)

		return sendRequest(true, createUrl(getECommerceDomainByEnv(), `/v3/users/me/payment-methods`), 'get', null, {
			...authHeader,
			'tictuk-tenant-id': tenantId,
		})
	},
	deleteCreditCard: async (cardId: string, Account: any, channelType: BackendChannel) => {
		if (!cardId) {
			throw new Error('apiPayments.deleteCreditCard: missing card ID')
		}
		const authHeaders = await Account.getAuthorizationHeader(channelType)
		const tenantId = getTenantInfo()

		return sendRequest(true, createUrl(getECommerceDomainByEnv(), `/v3/users/me/payment-methods/${cardId}`), 'delete', null, {
			...authHeaders,
			'tictuk-tenant-id': tenantId,
		})
	},
	setCreditCardAsDefault: async (tenantId: string, cardId: string, Account: any, channelType: BackendChannel) => {
		if (!tenantId) {
			throw new Error('apiPayments.setCreditCardAsDefault: missing tenant ID')
		}
		if (!cardId) {
			throw new Error('apiPayments.setCreditCardAsDefault: missing card ID')
		}
		const authHeaders = await Account.getAuthorizationHeader(channelType)
		return sendRequest(
			true,
			createUrl(getECommerceDomainByEnv(), `/v3/users/me/payment-methods/${cardId}`),
			'patch',
			{
				default: true,
			},
			{ ...authHeaders, 'tictuk-tenant-id': tenantId }
		)
	},
}

export default apiPayments
